import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Styled from 'styled-components';
import { Icon } from '@iconify/react';

import PresetIcons from '../common/PresetIcons';
import { NekoTooltip } from './Tooltip';
import { useClasses } from '../hooks';

const StyledIconContainer = Styled.div`
  display: flex;
  align-items: center;
`;

const hoverColor = (color, hoverColor) => {
  if (hoverColor) {
    return `
    &:hover {
      path {
        fill: ${hoverColor};
      }
    }
    `;
  }
  if (color) {
    return `
    &:hover {
      path {
        filter: brightness(1.03);
      }
    }
    `;
  }
};

const StyledIcon = Styled(Icon)`
  path {
    fill: ${props => props.color};
  }

  ${props => hoverColor(props.color, props.hoverColor)}

  &.neko-clickable {
    cursor: pointer;
  }

  &.spin {
    animation-name: spin;
    animation-duration: 700ms;
    animation-iteration-count: infinite;
    animation-timing-function: linear;

    @keyframes spin {
      0% {
        transform: rotate(360deg);
      }
      100% {
        transform: rotate(0deg);
      }
    }
  }

  &.busy {
    animation: crescendo 0.4s alternate infinite;
    @keyframes crescendo {
      0%   {transform: scale(.65);}
      100% {transform: scale(1);}
    }
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.35;
    cursor: default;
  }
`;

const StyledDiv = Styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding-left: 2px;

  img {
    width: auto !important;
    height: 20px !important;
  }
`;

const variants = {
  primary: {
    color: 'var(--neko-blue)',
  },
  success: {
    color: 'var(--neko-green)',
  },
  warning: {
    color: 'var(--neko-yellow)',
  },
  danger: {
    color: 'var(--neko-red)',
  }
}

const StyledNekoIcon = props => {

  let { icon, spinning = false, className = '', title, tooltip, containerStyle, color, hoverColor,
    disabled = false, raw, isBusy = false, variant, ...rest } = props;

  const iconColor = variant && variants[variant] ? variants[variant].color : color;
  const iconHoverColor = variant && variants[variant] ? variants[variant].hoverColor : hoverColor;

  if (!rest.width && !rest.height) {
    rest.width = rest.height = 30;
  }

  const finalIcon = useMemo(() => {
    if (typeof icon === 'string') {
      if (PresetIcons[icon]) {
        return PresetIcons[icon];
      }
    }
    return icon;
  }, [icon]);

  const isIconifyIcon = useMemo(() => {
    return !!PresetIcons[icon] || typeof icon === 'object';
  }, [icon]);


  const iconClassName = useClasses('neko-icon', className, { 'neko-clickable': !!rest.onClick }, { spin: spinning }, { disabled }, { busy: isBusy });

  if (tooltip) {
    return (
      <NekoTooltip text={tooltip.text} position={tooltip.position || 'top'}>
        <StyledIconContainer style={containerStyle}>
          <StyledIcon icon={finalIcon} className={iconClassName} color={iconColor} hoverColor={iconHoverColor} {...rest}/>
        </StyledIconContainer>
      </NekoTooltip>
    );
  }

  if (raw) {
    if (isIconifyIcon) {
      return <StyledIcon icon={finalIcon} className={iconClassName} color={iconColor} hoverColor={iconHoverColor} {...rest} />
    }
    return <StyledDiv>{finalIcon}</StyledDiv>
  }

  return (
    <StyledIconContainer style={containerStyle} title={title}>
      {isIconifyIcon && <StyledIcon icon={finalIcon} className={iconClassName}
        color={iconColor} hoverColor={iconHoverColor} {...rest} />}
      {!isIconifyIcon && <StyledDiv className={iconClassName}
        color={iconColor} hoverColor={iconHoverColor} {...rest}>{finalIcon}</StyledDiv>}
      {!tooltip ? null : <NekoTooltip>{tooltip}</NekoTooltip>}
    </StyledIconContainer>
  );
};

const NekoIcon = (props) => {
  return (<StyledNekoIcon {...props} />);
};

NekoIcon.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Icon), PropTypes.oneOf(['lock' ,'lock-open' ,'file-undo' ,'chevron-double-left' ,'chevron-double-right' ,'chevron-left' ,'chevron-right' ,'chevron-down' ,'chevron-up' ,'pause' ,'play' ,'replay' ,'check' ,'stop' ,'delete' ,'undo' ,'alert' ,'database' ,'pencil' ,'tools' ,'cog' ,'close' ,'cat' ,'upload'])]),
  color: PropTypes.string,
  spinning: PropTypes.bool,
  className: PropTypes.string,
  tooltip: PropTypes.string,
  raw: PropTypes.bool,
  isBusy: PropTypes.bool,
  variant: PropTypes.string,
};

NekoIcon.defaultProps = {
  icon: undefined,
  color: undefined,
  spinning: false,
  className: '',
  tooltip: undefined,
  raw: undefined,
  isBusy: false,
  variant: undefined,
};

export { NekoIcon };
